import type { PublishedPostDto } from "@nlc/shared-types";

import dayjs from "dayjs";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Avatar } from "@/components/avatar";
import { Button } from "@/components/button";

import { PostCaption } from "../../schedule/post-preview/PostCaption";
import { PostImage } from "../../schedule/post-preview/PostImage";
import { toScheduledPost } from "../../schedule/scheduledPost";
import { SOCIAL_PLATFORM_LABELS } from "../../socialPlatformLabels";
import { POST_FORMAT_LABELS } from "../postFormatLabels";

export function PostSummaryCard({ post }: { post: PublishedPostDto }) {
  const [isCaptionExpanded, setIsCaptionExpanded] = useState(false);
  const platformLabel = post.page
    ? SOCIAL_PLATFORM_LABELS[post.page.platform]
    : null;

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-border-secondary bg-bg-primary">
      <header className="flex items-center gap-lg p-xl">
        <Avatar
          alt={post.brand.name}
          className="size-10 rounded-full"
          src={post.brand.profilePicture ?? undefined}
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-lg font-semibold text-text-primary">
            {post.brand.name}
          </span>
          <span className="truncate text-sm text-text-tertiary">
            {[platformLabel ?? "Disconnected", POST_FORMAT_LABELS[post.format]]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </header>

      <PostImage post={toScheduledPost(post)} />

      <div className="flex flex-col gap-lg p-xl">
        <h2 className="text-lg font-semibold text-text-primary">
          {post.name || post.designs[0]?.name || post.brand.name}
        </h2>
        {post.caption && (
          <div className="flex flex-col items-start gap-xs">
            <p
              className={twMerge(
                "text-sm whitespace-pre-line break-words text-text-secondary",
                !isCaptionExpanded && "line-clamp-3",
              )}
            >
              <PostCaption caption={post.caption} />
            </p>
            <Button
              className="p-0"
              onPress={() => setIsCaptionExpanded(!isCaptionExpanded)}
              variant="unstyled"
            >
              <span className="text-sm font-semibold text-text-brand-secondary">
                {isCaptionExpanded ? "Show less" : "Read more"}
              </span>
            </Button>
          </div>
        )}
        {post.publishedAt && (
          <p className="text-sm text-text-tertiary">
            Published{" "}
            {dayjs(post.publishedAt).format("MMM D, YYYY [at] h:mm A")}
          </p>
        )}
        {post.permalink && platformLabel && (
          <Button asChild color="gray" variant="secondary">
            <a href={post.permalink} rel="noreferrer" target="_blank">
              Open on {platformLabel}
              <AccessibleIcon>
                <ExternalLink />
              </AccessibleIcon>
            </a>
          </Button>
        )}
      </div>
    </article>
  );
}
